/**
 * `WhatsAppPairingPanel` — scan-to-pair for the agent's WhatsApp number.
 *
 * ## Why the QR is fetched, never asked for
 *
 * The previous flow prefilled a chat prompt and hoped the concierge would echo
 * the QR back as a data-URI markdown image. That is **unfixable**, and not for
 * want of a better renderer: delivery depended on an LLM reproducing a
 * ~5,900-character base64 blob verbatim. The lexruntime lane CRC-verified two
 * attempts — one truncated outright, and one that walked every structural check
 * (`IHDR → IDAT → IDAT → IEND`, all CRCs valid, `complete: true`) and still
 * rendered an unscannable quarter-image. A corruption that passes validation is
 * worse than one that fails it. So the QR comes straight from the runtime over
 * HTTP and never round-trips through a model.
 *
 * ## The countdown is not decoration
 *
 * The QR has a hard 3-minute TTL, and a stale one fails **silently** — WhatsApp
 * shows nothing useful, leaving the lawyer in an "I scanned it and nothing
 * happened" loop. The deadline is therefore always on screen.
 *
 * `expiresAt` is trustworthy across refreshes: the runtime returns the CACHED QR
 * within the TTL without resetting its start time, so re-requesting does not
 * silently extend the deadline the lawyer is reading.
 *
 * ## `connected` is endpoint 1 of 2 — never a success state
 *
 * A scanned QR means the ASSISTANT has its own WhatsApp door. It does NOT mean
 * the channel is usable: until the lawyer binds their OWN number as the command
 * door, the assistant answers everyone there as a receptionist and nobody —
 * including the lawyer — can command it. Galvão sat in exactly that state:
 * paired 03:14, a real message at 03:30, receptionist branch, live and
 * ownerless. So `"pareado e ativo ✓"` was a true statement about the socket and
 * a false one about the product.
 *
 * This panel therefore treats `connected` as a TRANSITION into endpoint 2 and
 * says "Etapa 1 de 2". Only `SetupStatus.channelComplete.whatsapp` may be
 * rendered as ready — and that is owned by the checklist, not by this panel.
 *
 * NOT enforced by blocking: the command door is closed by sending a code INTO
 * the assistant's number, so the half-open channel MUST keep accepting inbound.
 * "Not activated" is a status-and-copy truth, never a transport block.
 *
 * ## Two destructive acts, deliberately never one button
 *
 * Asking for a QR while WhatsApp is live (`force`) STOPS the channel
 * immediately for the whole 3-minute window — the runtime's `web.login.start`
 * calls `stopChannel()` unconditionally before issuing the QR. If the lawyer
 * never scans, they are left with a dead channel. Credentials survive, so this
 * is recoverable by scanning. That is a different act from Disconnect (which
 * erases credentials) and gets its own confirm and its own words.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  getWhatsAppConnectStatus,
  isTerminalWaStatus,
  startWhatsAppConnect,
  type WaConnectEnvelope,
  type WaConnectStatus,
} from "@/api/lx-openclaw-config";

/** Poll cadence while a QR is live. Matches `OwnerPairingPanel`'s 3 s. */
const POLL_MS = 3000;

/** Every status this panel knows how to render. Anything else falls through to
 *  the unrecognised-status branch instead of rendering a title with no body. */
const KNOWN: ReadonlySet<string> = new Set<WaConnectStatus>([
  "awaiting_scan",
  "connected",
  "already_linked",
  "expired",
  "unstable",
  "disconnected",
  "error",
]);

/** Formats the remaining TTL as `m:ss`, floored at zero. */
export function formatRemaining(msRemaining: number): string {
  const total = Math.max(0, Math.floor(msRemaining / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

export function WhatsAppPairingPanel() {
  const { t } = useTranslation();
  const [env, setEnv] = useState<WaConnectEnvelope | null>(null);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  /** Relink confirm — a QR request that would kill a live channel. */
  const [confirmRelink, setConfirmRelink] = useState(false);
  const [now, setNow] = useState(() => Date.now());

  const pollTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const stopPolling = useCallback(() => {
    if (pollTimer.current) clearInterval(pollTimer.current);
    pollTimer.current = null;
  }, []);

  // Always clear on unmount — a poll firing into a closed dialog is a leak.
  useEffect(() => stopPolling, [stopPolling]);

  // 1 s heartbeat purely to re-render the countdown. Runs ONLY while a QR is
  // live, so a settled dialog is not ticking in the background.
  const qrLive = env?.status === "awaiting_scan" && !!env.expiresAt;
  useEffect(() => {
    if (!qrLive) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [qrLive]);

  const beginPolling = useCallback(() => {
    stopPolling();
    pollTimer.current = setInterval(() => {
      void getWhatsAppConnectStatus()
        .then((next) => {
          setEnv(next);
          // Every status except awaiting_scan/unstable is terminal, so the
          // spinner cannot hang; awaiting_scan self-terminates at expiresAt.
          if (isTerminalWaStatus(next.status)) stopPolling();
        })
        .catch(() => {
          // A transient poll failure is not a pairing failure — the next tick
          // re-reads. Only an explicit `error` status ends the attempt.
        });
    }, POLL_MS);
  }, [stopPolling]);

  const request = useCallback(
    async (force: boolean) => {
      setBusy(true);
      setFailure(null);
      setConfirmRelink(false);
      try {
        const next = await startWhatsAppConnect({ force });
        setEnv(next);
        setNow(Date.now());
        if (!isTerminalWaStatus(next.status)) beginPolling();
      } catch (err) {
        setFailure(err instanceof Error ? err.message : String(err));
      } finally {
        setBusy(false);
      }
    },
    [beginPolling],
  );

  const status = env?.status ?? null;
  const remainingMs = env?.expiresAt ? Date.parse(env.expiresAt) - now : 0;
  // Treat a lapsed countdown as expired locally so the UI does not sit on a
  // dead QR for up to one poll interval telling the lawyer to scan it.
  const locallyExpired = qrLive && remainingMs <= 0;

  return (
    <div
      className="rounded border border-slate-200 p-3 dark:border-slate-800"
      data-testid="whatsapp-pairing-panel"
      data-status={locallyExpired ? "expired" : (status ?? "idle")}
    >
      <h4 className="mb-1 text-sm font-semibold">
        {t("channels.wa.pairTitle", "Parear o WhatsApp do assistente")}
      </h4>
      <p className="mb-2 text-xs text-slate-500 dark:text-slate-400">
        {t(
          "channels.wa.pairWhy",
          "O WhatsApp exige um número dedicado (chip/eSIM) para o aparelho pareado. Gere o QR aqui e leia com o celular desse número.",
        )}
      </p>

      {/* IDLE — nothing requested yet. */}
      {!env && !busy && (
        <button
          type="button"
          data-testid="whatsapp-pair-start"
          onClick={() => void request(false)}
          className="rounded-md border border-coffee-800 bg-coffee-800 px-2.5 py-1 text-xs font-medium text-cream-50 hover:bg-coffee-900 dark:border-cream-100 dark:bg-cream-100 dark:text-coffee-900"
        >
          {t("channels.wa.generateQr", "Gerar QR code")}
        </button>
      )}

      {busy && (
        <p className="text-xs text-slate-500" data-testid="whatsapp-pair-busy">
          {t("channels.wa.working", "Falando com o runtime…")}
        </p>
      )}

      {/* AWAITING_SCAN — the QR + its deadline. */}
      {qrLive && !locallyExpired && (
        <div className="space-y-2">
          <img
            src={env.qrDataUrl}
            alt={t("channels.wa.qrAlt", "QR code para parear o WhatsApp")}
            data-testid="whatsapp-pair-qr"
            className="h-56 w-56 rounded bg-white p-2"
          />
          <p className="text-xs text-slate-600 dark:text-slate-300">
            {t(
              "channels.wa.scanHint",
              "No celular do assistente: WhatsApp → Aparelhos conectados → Conectar um aparelho.",
            )}
          </p>
          <p
            className="text-xs font-medium tabular-nums text-amber-700 dark:text-amber-300"
            data-testid="whatsapp-pair-countdown"
          >
            {t("channels.wa.expiresIn", "Expira em {{time}}", {
              time: formatRemaining(remainingMs),
            })}
          </p>
        </div>
      )}

      {/* CONNECTED — endpoint 1 only. A transition, not a terminus: the panel
          says so plainly and points at endpoint 2 (the owner pairing panel
          rendered directly below this one in the dialog). */}
      {status === "connected" && (
        <div
          className="space-y-1 rounded bg-emerald-500/10 p-2"
          data-testid="whatsapp-pair-connected"
        >
          <p className="text-xs font-medium text-emerald-700 dark:text-emerald-300">
            {t("channels.wa.step1Done", "Etapa 1 de 2 concluída — o assistente tem o próprio WhatsApp ✓")}
          </p>
          <p className="text-xs text-emerald-800/80 dark:text-emerald-200/80">
            {t(
              "channels.wa.step2Next",
              "Falta a etapa 2: parear o SEU número como porta de comando. Até lá o assistente atende quem escrever, mas ninguém consegue comandá-lo por ali — inclusive você.",
            )}
          </p>
        </div>
      )}

      {/* ALREADY_LINKED — refused without force. The relink confirm lives here. */}
      {status === "already_linked" && !confirmRelink && (
        <div className="space-y-2" data-testid="whatsapp-pair-already-linked">
          <p className="text-xs text-slate-600 dark:text-slate-300">
            {t("channels.wa.alreadyLinked", "Este WhatsApp já está pareado.")}
          </p>
          <button
            type="button"
            data-testid="whatsapp-pair-relink"
            onClick={() => setConfirmRelink(true)}
            className="rounded-md border border-amber-500 px-2.5 py-1 text-xs font-medium text-amber-700 hover:bg-amber-500/10 dark:text-amber-300"
          >
            {t("channels.wa.relink", "Parear outro aparelho")}
          </button>
        </div>
      )}

      {/* The relink confirm — its own copy, because the hazard is its own. */}
      {confirmRelink && (
        <div
          className="space-y-2 rounded border border-amber-500/50 bg-amber-500/10 p-2"
          data-testid="whatsapp-relink-confirm"
        >
          <p className="text-xs text-amber-800 dark:text-amber-200">
            {t(
              "channels.wa.relinkWarning",
              "Isso vai desconectar o WhatsApp atual imediatamente. Você precisará escanear o novo QR para reconectar.",
            )}
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              data-testid="whatsapp-relink-confirm-yes"
              onClick={() => void request(true)}
              className="rounded-md border border-amber-600 bg-amber-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-amber-700"
            >
              {t("channels.wa.relinkConfirm", "Desconectar e gerar novo QR")}
            </button>
            <button
              type="button"
              data-testid="whatsapp-relink-cancel"
              onClick={() => setConfirmRelink(false)}
              className="rounded-md border border-slate-300 px-2.5 py-1 text-xs dark:border-slate-700"
            >
              {t("channels.wa.cancel", "Cancelar")}
            </button>
          </div>
        </div>
      )}

      {/* EXPIRED — locally or per the runtime. Offer a fresh QR. */}
      {(status === "expired" || locallyExpired) && (
        <div className="space-y-2" data-testid="whatsapp-pair-expired">
          <p className="text-xs text-slate-600 dark:text-slate-300">
            {t("channels.wa.expired", "O QR expirou sem ser lido.")}
          </p>
          <button
            type="button"
            data-testid="whatsapp-pair-new-qr"
            onClick={() => void request(false)}
            className="rounded-md border border-slate-300 px-2.5 py-1 text-xs dark:border-slate-700"
          >
            {t("channels.wa.newQr", "Gerar novo QR")}
          </button>
        </div>
      )}

      {/* UNSTABLE — non-terminal; the poller is still running. */}
      {status === "unstable" && (
        <p className="text-xs text-slate-500" data-testid="whatsapp-pair-unstable">
          {t("channels.wa.unstable", "Estabilizando a conexão… aguarde.")}
        </p>
      )}

      {/* DISCONNECTED — idle/terminal, e.g. straight after a logout. */}
      {status === "disconnected" && (
        <div className="space-y-2" data-testid="whatsapp-pair-disconnected">
          <p className="text-xs text-slate-600 dark:text-slate-300">
            {t("channels.wa.disconnected", "WhatsApp não está pareado.")}
          </p>
          <button
            type="button"
            data-testid="whatsapp-pair-retry"
            onClick={() => void request(false)}
            className="rounded-md border border-slate-300 px-2.5 py-1 text-xs dark:border-slate-700"
          >
            {t("channels.wa.generateQr", "Gerar QR code")}
          </button>
        </div>
      )}

      {/* ERROR — surface the runtime's own message; it is written for humans. */}
      {status === "error" && (
        <p
          className="rounded bg-red-500/10 p-2 text-xs text-red-700 dark:text-red-300"
          data-testid="whatsapp-pair-error"
        >
          {env?.message || t("channels.wa.genericError", "Não foi possível parear.")}
        </p>
      )}

      {/* UNRECOGNISED status — an older runtime (the legacy `/connect` returned
          `pending`) or a contract that moved ahead of this build. Without this
          branch the panel would render a title and nothing else: a silent dead
          end with no way forward, which is exactly the failure mode this whole
          dialog exists to replace. */}
      {status && !KNOWN.has(status) && (
        <div className="space-y-2" data-testid="whatsapp-pair-unknown-status">
          <p className="rounded bg-amber-500/10 p-2 text-xs text-amber-800 dark:text-amber-200">
            {env?.message ||
              t(
                "channels.wa.unknownStatus",
                "Este runtime respondeu de um jeito que esta tela ainda não entende. Atualize o runtime e tente de novo.",
              )}
          </p>
          <button
            type="button"
            data-testid="whatsapp-pair-retry-unknown"
            onClick={() => void request(false)}
            className="rounded-md border border-slate-300 px-2.5 py-1 text-xs dark:border-slate-700"
          >
            {t("channels.wa.tryAgain", "Tentar de novo")}
          </button>
        </div>
      )}

      {/* A TRANSPORT failure (route missing, network) — distinct from a pairing
          `error` status, so the lawyer can tell "the runtime is old" from
          "the pairing failed". */}
      {failure && (
        <p
          className="mt-2 rounded bg-red-500/10 p-2 text-xs text-red-700 dark:text-red-300"
          data-testid="whatsapp-pair-transport-error"
        >
          {failure}
        </p>
      )}
    </div>
  );
}
